import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../services/firebase";
import { useLocation, useNavigate } from "react-router-dom";
import HRCampusSelector from "../../components/HRCampusSelector";
import { getCurrentUser } from "../../auth";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function payrollLsKey(campus) {
  return `payroll_${String(campus || "gen").toLowerCase()}`;
}
function loadLocalPayroll(campus) {
  try {
    const raw = localStorage.getItem(payrollLsKey(campus));
    return raw ? JSON.parse(raw) : [];
  } catch (err) {
    return [];
  }
}

export default function PayslipPrint() {
  const q = useQuery();
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const roleStr = String(currentUser?.role || "").toLowerCase();
  const rolesArr = Array.isArray(currentUser?.roles) ? currentUser.roles.map(r => String(r).toLowerCase()) : [];
  const hasRole = (r) => roleStr === r || rolesArr.includes(r);
  const isExec = hasRole("admin") || hasRole("superadmin") || hasRole("vicepresident") || hasRole("vp") || hasRole("board") || hasRole("bot") || String(currentUser?.campus || "").toLowerCase() === "all";
  const canReadAllPayroll = isExec || hasRole("finance");
  const [selectedCampus, setSelectedCampus] = useState(q.get("campus") || "bulacan");
  const selectedPeriod = q.get("period") || "";
  const onlyEmployee = q.get("employee") || "";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const payrollCollection = collection(db, "payroll");
        let ref = payrollCollection;
        const campusToUse = canReadAllPayroll ? selectedCampus : String(currentUser?.campus || selectedCampus || "").toLowerCase();
        if (campusToUse) {
          ref = query(ref, where("campus", "==", String(campusToUse).toLowerCase()));
        }
        if (selectedPeriod) {
          ref = query(ref, where("payPeriod", "==", selectedPeriod));
        }
        const payrollSnapshot = await getDocs(ref);
        const list = payrollSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        let filtered = list.sort((a,b) => {
          const key = (n) => {
            const s = String(n || "").trim();
            if (!s) return "";
            let last = "";
            if (s.includes(",")) last = s.split(",")[0].trim().toLowerCase();
            else { const parts = s.split(/\s+/); last = (parts[parts.length-1]||"").toLowerCase(); }
            const full = s.replace(/,/g,"").toLowerCase();
            return `${last}|${full}`;
          };
          return key(a.employeeName).localeCompare(key(b.employeeName));
        });
        if (onlyEmployee) filtered = filtered.filter(r => (r.employeeName || "").toLowerCase().includes(onlyEmployee.toLowerCase()));
        setRows(filtered);
      } catch (err) {
        const localCampus = canReadAllPayroll ? selectedCampus : String(currentUser?.campus || selectedCampus || "").toLowerCase();
        let local = loadLocalPayroll(localCampus).filter(r => {
          const rc = String(r.campus || "").toLowerCase();
          const sc = String(localCampus || "").toLowerCase();
          const rp = String(r.payPeriod || "");
          const sp = String(selectedPeriod || "");
          const campusOk = !sc || rc === sc;
          const periodOk = !sp || rp === sp;
          return campusOk && periodOk;
        }).sort((a,b) => {
          const key = (n) => {
            const s = String(n || "").trim();
            if (!s) return "";
            let last = "";
            if (s.includes(",")) last = s.split(",")[0].trim().toLowerCase();
            else { const parts = s.split(/\s+/); last = (parts[parts.length-1]||"").toLowerCase(); }
            const full = s.replace(/,/g,"").toLowerCase();
            return `${last}|${full}`;
          };
          return key(a.employeeName).localeCompare(key(b.employeeName));
        });
        if (onlyEmployee) local = local.filter(r => (r.employeeName || "").toLowerCase().includes(onlyEmployee.toLowerCase()));
        setRows(local);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [selectedCampus, selectedPeriod, onlyEmployee, canReadAllPayroll]);

  const periodLabel = useMemo(() => {
    if (!selectedPeriod) return "Selected Period";
    try {
      const d = new Date(selectedPeriod);
      const y = d.getFullYear();
      const m = d.toLocaleString(undefined, { month: "long" });
      const start = d.getDate();
      const end = start === 1 ? 15 : new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
      return `${m} ${start}\u2013${end}, ${y}`;
    } catch {
      return selectedPeriod;
    }
  }, [selectedPeriod]);

  function getHeaderImage() {
    const c = String(selectedCampus || "").toLowerCase();
    // Allow override via localStorage for quick fixes
    try {
      const customBul = localStorage.getItem("payslip_header_bulacan");
      const customGap = localStorage.getItem("payslip_header_gapan");
      if (c.includes("gapan") && customGap) return customGap;
      if ((c.includes("bulacan") || c.includes("sjdm")) && customBul) return customBul;
    } catch (e) { /* ignore */ }
    if (c.includes("gapan")) return "/headers/gapan-header.png"; // expected in public/headers/
    if (c.includes("bulacan") || c.includes("sjdm")) return "/headers/bulacan-header.png"; // expected in public/headers/
    return "/headers/bulacan-header.png";
  }

  function slipHtml(r) {
    const basic = Number(r.basicPay || 0);
    const overtime = Number(r.overtime || 0);
    const substitution = Number(r.substitution || 0);
    const absences = Number(r.absences || 0);
    const gross = basic + overtime + substitution;
    const startDay = Number(String(r.payPeriod || "").split("-")[2] || 0);
    const isFirstCutoff = startDay === 1;
    const sss = isFirstCutoff ? Number(r.sss || 0) : 0;
    const philhealth = isFirstCutoff ? Number(r.philhealth || 0) : 0;
    const pagibig = !isFirstCutoff ? Number(r.pagIbig || 0) : 0;
    const bir = !isFirstCutoff ? Number(r.bir || 0) : 0;
    const booksMisc = Number(r.booksMisc || 0);
    const contri = Number(r.contri || 0);
    const coop = Number(r.coop || 0);
    const uniform = Number(r.uniform || 0);
    const cashAdvance = Number(r.cashAdvance || 0);
    const totalDeduction = sss + philhealth + pagibig + bir + booksMisc + contri + coop + uniform + cashAdvance + Math.max(0, absences);
    const net = gross - totalDeduction;
    const banner = getHeaderImage();
    const fallbackText = (() => {
      const c = String(selectedCampus || "").toLowerCase();
      if (c.includes("gapan")) return "Village Montessori School (Gapan, Nueva Ecija), Inc.";
      return "Village Montessori School and Colleges";
    })();
    return `
      <div class="slip">
        <div class="slip-head">
          <img class="slip-banner" src="${banner}" alt="School Header" onerror="this.style.display='none'; var f=this.nextElementSibling; if(f){f.style.display='block';}" />
          <div class="slip-fallback" style="display:none">${fallbackText}</div>
          <div class="slip-title">Payslip</div>
          <div class="slip-sub">Campus: ${String(selectedCampus || "").toUpperCase()} &nbsp;&middot;&nbsp; Period: ${periodLabel}</div>
        </div>
        <div class="slip-body">
          <div class="row">
            <div><strong>Name:</strong> ${r.employeeName || ""}</div>
            <div><strong>Position:</strong> ${r.position || ""}</div>
          </div>
          <div class="cols">
            <div class="col">
              <div class="sec-title">Compensation</div>
              <div class="kv"><span>Basic Pay</span><span>₱${basic.toLocaleString()}</span></div>
              <div class="kv"><span>Overtime</span><span>₱${overtime.toLocaleString()}</span></div>
              <div class="kv"><span>Substitution</span><span>₱${substitution.toLocaleString()}</span></div>
              <div class="kv"><span>Gross Pay</span><span>₱${gross.toLocaleString()}</span></div>
            </div>
            <div class="col">
              <div class="sec-title">Deductions</div>
              <div class="kv"><span>SSS</span><span>₱${sss.toLocaleString()}</span></div>
              <div class="kv"><span>PhilHealth</span><span>₱${philhealth.toLocaleString()}</span></div>
              <div class="kv"><span>PAG-IBIG</span><span>₱${pagibig.toLocaleString()}</span></div>
              <div class="kv"><span>BIR</span><span>₱${bir.toLocaleString()}</span></div>
              <div class="kv"><span>Books & Misc.</span><span>₱${booksMisc.toLocaleString()}</span></div>
              <div class="kv"><span>Contri</span><span>₱${contri.toLocaleString()}</span></div>
              <div class="kv"><span>COOP</span><span>₱${coop.toLocaleString()}</span></div>
              <div class="kv"><span>Uniform</span><span>₱${uniform.toLocaleString()}</span></div>
              <div class="kv"><span>Cash Advance</span><span>₱${cashAdvance.toLocaleString()}</span></div>
              <div class="kv"><span>Absences/Undertime</span><span>₱${absences.toLocaleString()}</span></div>
              <div class="kv total"><span>Total Deductions</span><span>₱${totalDeduction.toLocaleString()}</span></div>
            </div>
          </div>
          <div class="net"><span>Net Pay</span><span>₱${net.toLocaleString()}</span></div>
        </div>
        <div class="slip-sign">
          <div>
            <div class="line"></div>
            <div class="name">Prepared by: Catalina T. Basa</div>
            <div class="title">Finance Officer</div>
          </div>
          <div>
            <div class="line"></div>
            <div class="name">Received by:</div>
            <div class="title">Signature over Printed Name / Date</div>
          </div>
        </div>
      </div>
    `;
  }

  function buildStyles(pageSize, includeColorAdjust = true) {
    const page = pageSize === "8x4" ? "8.5in 4in" : "letter";
    return `
      <style>
        @page { size: ${page} landscape; margin: ${pageSize === "8x4" ? "0.25in" : "0.5in"}; }
        body { font-family: Arial, sans-serif; color: #000; ${includeColorAdjust ? "-webkit-print-color-adjust: exact; print-color-adjust: exact;" : ""} }
        .sheet { width: 100%; }
        .slip { width: 8.5in; height: 4in; border: 1px solid #000; padding: 10px 14px; box-sizing: border-box; }
        .pair { display: flex; flex-direction: column; gap: 0.5in; page-break-after: always; }
        .slip-head { text-align: center; margin-bottom: 6px; }
        .slip-banner { width: 100%; height: auto; object-fit: contain; margin-bottom: 4px; }
        .slip-title { font-size: 16px; font-weight: 700; text-transform: uppercase; }
        .slip-sub { font-size: 11px; color: #555; }
        .slip-body .row { display: flex; justify-content: space-between; font-size: 12px; margin: 6px 0; }
        .cols { display: flex; gap: 24px; }
        .col { flex: 1; }
        .sec-title { font-weight: 700; margin: 6px 0; border-bottom: 1px solid #000; }
        .kv { display: flex; justify-content: space-between; font-size: 12px; }
        .kv.total { font-weight: 700; border-top: 1px dashed #000; margin-top: 4px; padding-top: 4px; }
        .net { display: flex; justify-content: space-between; font-weight: 800; margin-top: 6px; font-size: 13px; }
        .slip-sign { display: flex; justify-content: space-between; margin-top: 10px; gap: 40px; font-size: 11px; }
        .line { border-top: 1px solid #000; height: 18px; margin: 18px 0 6px 0; }
      </style>
    `;
  }

  function printTwoPerLetter() {
    if (loading) return;
    if (!rows || rows.length === 0) {
      const msg = "<p style='font-family:Arial;padding:20px'>No payslips found for the selected campus and period.</p>";
      try {
        const w = window.open("", "_blank", "noopener,noreferrer");
        if (w && w.document) { w.document.write(msg); w.document.close(); }
      } catch (e) { console.warn("Payslip: no rows to print"); }
      return;
    }
    const style = buildStyles("letter");
    const content = rows.map(r => `<div class="pair">${slipHtml(r)}${slipHtml(r)}</div>`).join("");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8" />${style}</head><body class="sheet">${content}</body></html>`;
    // Try opening a new window for clarity; fallback to iframe if blocked
    try {
      const w = window.open("", "_blank", "noopener,noreferrer");
      if (w && w.document) {
        w.document.open(); w.document.write(html); w.document.close();
        setTimeout(() => { try { w.focus(); w.print(); } catch (e) { console.warn("window print failed"); } }, 300);
        return;
      }
    } catch (err) { console.warn("Popup blocked; using iframe print"); }
    try {
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow && iframe.contentWindow.document;
      if (!doc) {
        console.warn("iframe document unavailable");
        return;
      }
      doc.open(); doc.write(html); doc.close();
      setTimeout(() => {
        try { iframe.contentWindow && iframe.contentWindow.print(); } catch (e) { console.warn("iframe print failed"); }
        setTimeout(() => { try { document.body.removeChild(iframe); } catch (e) { void 0; } }, 800);
      }, 400);
    } catch (e) { console.warn("2-per-letter print failed"); }
  }

  function printSingleSlip() {
    if (loading) return;
    if (!rows || rows.length === 0) {
      try { alert("No payslips found for the selected campus and period."); } catch (e) { console.warn("Alert failed"); }
      return;
    }
    const style = buildStyles("8x4");
    const content = rows.map(r => `<div class="pair" style="gap:0.25in">${slipHtml(r)}${slipHtml(r)}</div>`).join("");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8" />${style}</head><body class="sheet">${content}</body></html>`;
    try {
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow && iframe.contentWindow.document;
      if (!doc) {
        window.print();
        return;
      }
      doc.open();
      doc.write(html);
      doc.close();
      setTimeout(() => {
        try { iframe.contentWindow && iframe.contentWindow.print(); } catch (e) { void 0; }
        setTimeout(() => {
          try { document.body.removeChild(iframe); } catch (e) { void 0; }
        }, 800);
      }, 400);
    } catch (err) {
      try { window.print(); } catch (e) { console.warn("window.print failed"); }
    }
  }

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3 print-hide">
        <div className="d-flex align-items-center gap-3">
          <h4 className="mb-0">Payslip Print Preview</h4>
          <HRCampusSelector selectedCampus={selectedCampus} onChange={setSelectedCampus} />
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={() => navigate(-1)}>Back</button>
          <button className="btn btn-primary" onClick={printTwoPerLetter} disabled={loading || rows.length === 0}>Print (2 per Letter)</button>
          <button className="btn btn-outline-primary" onClick={printSingleSlip} disabled={loading || rows.length === 0}>Print All 8.5×4 (2 copies)</button>
          <button
            className="btn btn-outline-secondary"
            onClick={() => {
              if (loading || rows.length === 0) return;
              const style = buildStyles("letter", false);
              const content = rows.map(r => `<div class="pair">${slipHtml(r)}${slipHtml(r)}</div>`).join("");
              const html = `<!DOCTYPE html><html><head><meta charset="utf-8" />${style}</head><body class="sheet">${content || "<p style='font-family:Arial;padding:20px'>No payslips found.</p>"}</body></html>`;
              try {
                const w = window.open("", "_blank", "noopener,noreferrer");
                if (w && w.document) { w.document.open(); w.document.write(html); w.document.close(); }
              } catch (err) { console.warn("Preview window blocked"); }
            }}
            title="Open a preview tab to verify layout before printing"
            disabled={loading || rows.length === 0}
          >
            Preview 2 per Letter
          </button>
        </div>
      </div>
      <div className="mb-3">
        <h5 className="mb-1">Period — {periodLabel}</h5>
        <div className="text-muted">Campus: {selectedCampus.toUpperCase()}</div>
      </div>
      <div className="row g-3">
        {loading ? (
          <div className="text-muted">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-muted">No payroll records found for the selected filter</div>
        ) : rows.map(r => (
          <div key={r.id} className="col-12">
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <div>
                    <div className="fw-bold">{r.employeeName}</div>
                    <div className="text-muted small">Net Pay: ₱{Number(r.amountToBePaid || 0).toLocaleString()}</div>
                  </div>
                  <div className="text-end">
                    <button className="btn btn-sm btn-outline-primary" onClick={() => {
                      const temp = { ...r };
                      const html = `<html><head>${buildStyles("8x4")}</head><body class="sheet"><div class="pair" style="gap:0.25in">${slipHtml(temp)}${slipHtml(temp)}</div></body></html>`;
                      try {
                        const w = window.open("", "_blank", "noopener,noreferrer");
                        if (!w) return;
                        w.document.open(); w.document.write(html); w.document.close();
                        setTimeout(() => { try { w.print(); w.close(); } catch (e) { void 0; } }, 300);
                      } catch (e) { void 0; }
                    }}>Print this employee</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
